/**
 * CartDrawer — slide-out cart panel. Orchestrates cart state, route-change
 * auto-close, specials fetching, and the BOGO-deal stack. Line items and
 * footer-totals are handed off to dedicated sub-components.
 */
import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { X, Sparkles, Check } from "lucide-react";
import { useCart, formatMoney } from "../lib/cart";
import { getSpecials } from "../lib/api";
import {
    activeBogoProgresses,
    appliedDealNames,
    appliedDealIds,
} from "../lib/promos";
import { useLoyalty } from "../lib/loyalty";
import CartDealCard from "./CartDealCard";
import CartLineItem from "./cart/CartLineItem";
import CartFooter from "./cart/CartFooter";

export default function CartDrawer() {
    const { open, setOpen, checkout, storeSlug, updateQty, removeItem, loading } =
        useCart();
    const { points: loyaltyPoints, openModal: openLoyalty } = useLoyalty();
    const [specials, setSpecials] = useState([]);
    const location = useLocation();

    // Auto-close drawer on route change so internal links don't leave the
    // drawer covering the new page on mobile. Microtask wrap satisfies the
    // set-state-in-effect lint rule.
    useEffect(() => {
        Promise.resolve().then(() => setOpen(false));
    }, [location.pathname, setOpen]);

    useEffect(() => {
        if (!storeSlug) return;
        getSpecials(storeSlug)
            .then((d) => setSpecials(d?.specials || []))
            .catch(() => setSpecials([]));
    }, [storeSlug]);

    const items = useMemo(() => checkout?.items || [], [checkout?.items]);
    const ps = checkout?.priceSummary;

    const bogoProgressList = useMemo(
        () => activeBogoProgresses(specials, items),
        [specials, items]
    );
    // Dutchie is the source of truth for "what discount applies". If
    // priceSummary.discounts > 0, a special is locked in. Resolve the actual
    // Dutchie special.name(s) of the deal(s) that fired by intersecting each
    // discounted line's variant.special*Ids with the store's specials list.
    const discountApplied = (ps?.discounts || 0) > 0;
    const appliedNames = useMemo(
        () => (discountApplied ? appliedDealNames(specials, items) : []),
        [discountApplied, specials, items]
    );
    const appliedIds = useMemo(
        () => (discountApplied ? appliedDealIds(specials, items) : new Set()),
        [discountApplied, specials, items]
    );
    // Per-deal suppression: hide ONLY the progress cards for deals that have
    // already fired in Dutchie. Other eligible-but-uncompleted deals still
    // surface so the customer sees every remaining offer to chase.
    const pendingProgressList = useMemo(
        () => bogoProgressList.filter((p) => !appliedIds.has(p.id)),
        [bogoProgressList, appliedIds]
    );
    const showProgress = pendingProgressList.length > 0;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={() => setOpen(false)}
                className={`fixed inset-0 z-[120] bg-zen-ink/40 backdrop-blur-sm transition-opacity duration-300 ${
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden="true"
            />

            <aside
                data-testid="cart-drawer"
                aria-hidden={!open}
                className={`fixed top-0 right-0 bottom-0 z-[130] w-full sm:max-w-lg bg-zen-bg shadow-[-20px_0_60px_rgba(31,36,24,0.18)] flex flex-col transform transition-transform duration-300 ease-out ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                {/* Header */}
                <header className="flex items-center justify-between px-6 sm:px-8 py-6 border-b border-zen-ink/10">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive">
                            Your cart
                        </p>
                        <p className="font-serif text-2xl font-light text-zen-ink mt-1">
                            {items.length} item{items.length === 1 ? "" : "s"}
                        </p>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        data-testid="cart-close"
                        aria-label="Close cart"
                        className="p-2 text-zen-ink hover:text-zen-olive"
                    >
                        <X size={20} />
                    </button>
                </header>

                {/* ZenCircle loyalty strip — pinned high so points status is
                    always visible and the bottom of the cart breathes for
                    totals + checkout. */}
                <div
                    data-testid="cart-loyalty-pill"
                    className="border-b border-zen-olive/20 bg-zen-olive/5 px-6 sm:px-8 py-3 flex items-center justify-between gap-3"
                >
                    <div className="flex items-center gap-2 text-zen-ink">
                        <Sparkles size={13} className="text-zen-olive shrink-0" />
                        {loyaltyPoints != null ? (
                            <span className="text-xs leading-tight">
                                <span
                                    data-testid="cart-loyalty-points"
                                    className="font-semibold tabular-nums"
                                >
                                    {Number(loyaltyPoints).toLocaleString()}
                                </span>{" "}
                                ZenCircle pts available
                            </span>
                        ) : (
                            <span className="text-xs leading-tight">
                                Are you a ZenCircle member?
                            </span>
                        )}
                    </div>
                    <button
                        onClick={openLoyalty}
                        data-testid="cart-loyalty-check"
                        className="text-[10px] uppercase tracking-[0.22em] text-zen-olive hover:text-zen-olive-dark border-b border-zen-olive/40 pb-0.5"
                    >
                        {loyaltyPoints != null ? "Refresh" : "Check"}
                    </button>
                </div>

                {/* Items */}
                <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
                    {discountApplied && items.length > 0 && (
                        <div
                            data-testid="bogo-progress"
                            className="mb-6 p-4 border bg-emerald-50 border-emerald-300 text-emerald-900"
                        >
                            <div className="flex items-start gap-2">
                                <Check size={16} className="mt-0.5 shrink-0" />
                                <div className="text-sm leading-snug">
                                    {appliedNames.length > 0 ? (
                                        <>
                                            <div className="font-medium">
                                                Deal applied:{" "}
                                                <span
                                                    className="font-bold"
                                                    data-testid="applied-deal-name"
                                                >
                                                    {appliedNames.join(" + ")}
                                                </span>
                                            </div>
                                            <div className="mt-0.5 opacity-80">
                                                You&rsquo;re saving{" "}
                                                <span className="font-bold">
                                                    {formatMoney(ps?.discounts)}
                                                </span>
                                                .
                                            </div>
                                        </>
                                    ) : (
                                        <div className="font-medium">
                                            Deal applied — you&rsquo;re saving{" "}
                                            <span className="font-bold">
                                                {formatMoney(ps?.discounts)}
                                            </span>
                                            .
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showProgress && (
                        <div data-testid="cart-deals-stack" className="mb-5">
                            <p className="text-base sm:text-lg font-serif font-medium text-zen-ink mb-1 leading-tight">
                                Unlock more offers
                            </p>
                            <p className="text-xs text-zen-ink-soft mb-3 leading-snug">
                                You have items eligible for the deals below. Complete
                                them to stack more savings.
                            </p>
                            <div className="space-y-2.5">
                                {pendingProgressList.map((p) => (
                                    <CartDealCard
                                        key={p.id}
                                        progress={p}
                                        items={items}
                                        storeSlug={storeSlug}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {items.length === 0 ? (
                        <div className="text-center py-16">
                            <p className="text-zen-ink-soft text-sm mb-6">
                                Your cart is empty.
                            </p>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1 hover:border-zen-olive"
                            >
                                Browse menu
                            </button>
                        </div>
                    ) : (
                        <ul className="space-y-5">
                            {items.map((it) => (
                                <CartLineItem
                                    key={it.id}
                                    item={it}
                                    loading={loading}
                                    specials={specials}
                                    progresses={bogoProgressList}
                                    storeSlug={storeSlug}
                                    onIncrement={() => updateQty(it.id, it.quantity + 1)}
                                    onDecrement={() => updateQty(it.id, it.quantity - 1)}
                                    onRemove={() => removeItem(it.id)}
                                />
                            ))}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <CartFooter
                        priceSummary={ps}
                        storeSlug={storeSlug}
                        onClose={() => setOpen(false)}
                    />
                )}
            </aside>
        </>
    );
}
